import { useTranslation } from "react-i18next";
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, X, Check, Search, Briefcase } from 'lucide-react';
import { useMasterLabel } from '../../hooks/useMasterLabel';
import { useAnchoredDropdown } from './selectStyles';

// Per-row translated profession name used inside both selected tags + dropdown options.
const ProfessionName = ({ prof }) => {
    const id = prof?.professionId || prof?._id;
    const label = useMasterLabel('masterProfession', id, 'professionName', prof?.professionName || '');
    return <>{label}</>;
};

const ProfessionSelect = ({
    value,
    onChange,
    professions = [],
    loading = false,
    placeholder,
    disabled = false,
    error = false,
    className = '',
    maxSelections = 3,
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const containerRef = useRef(null);
    const searchInputRef = useRef(null);
    const panelOpen = isOpen && !disabled;
    const { anchorRef, panelRef, panelStyle } = useAnchoredDropdown(panelOpen);

    // Normalize value to always be an array
    const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);

    const selectedProfessions = selectedValues
        .map(v => professions?.find(p => p.professionId === v || p._id === v))
        .filter(Boolean);

    // Close on outside click. Testing panelRef as well as containerRef is critical
    // here: the panel is portaled out of the container, and this is a MULTI-select
    // that deliberately stays open between picks — without it, the first option
    // click would read as "outside" and close the panel, so choosing a second
    // profession would be impossible.
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
            setIsOpen(false);
            setSearchQuery('');
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen, panelRef]);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            setTimeout(() => searchInputRef.current?.focus(), 50);
        }
    }, [isOpen]);

    const filteredProfessions = professions.filter(p =>
        p.professionName?.toLowerCase().includes(searchQuery.toLowerCase().trim())
    );

    const handleSelect = (professionId) => {
        const isAlreadySelected = selectedValues.includes(professionId);
        let newValues;
        if (isAlreadySelected) {
            // Deselect
            newValues = selectedValues.filter(v => v !== professionId);
        } else {
            // Select - check max limit
            if (selectedValues.length >= maxSelections) return;
            newValues = [...selectedValues, professionId];
        }
        onChange(newValues);
    };

    const handleRemove = (e, professionId) => {
        e.stopPropagation();
        const newValues = selectedValues.filter(v => v !== professionId);
        onChange(newValues);
    };

    const handleClearAll = (e) => {
        e.stopPropagation();
        onChange([]);
        setIsOpen(false);
        setSearchQuery('');
    };

    const handleToggle = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
            if (isOpen) setSearchQuery('');
        }
    };

    return (
        <div ref={containerRef} className={`relative ${className}`} data-testid="profession-select-container">
            {/* Trigger Button */}
            <button
                ref={anchorRef}
                type="button"
                onClick={handleToggle}
                disabled={disabled}
                className={`w-full px-3 py-2.5 rounded-xl border text-sm font-medium transition-all duration-200 flex items-center gap-2 min-h-[44px] ${
                    error
                        ? 'border-red-500 bg-red-50'
                        : isOpen
                            ? 'border-[#4ABDBD] ring-2 ring-[#4ABDBD]/20 bg-white'
                            : 'border-gray-200 hover:border-[#4ABDBD] bg-white focus-within:ring-2 focus-within:ring-[#4ABDBD]'
                } ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'cursor-pointer'}`}
                data-testid="profession-select-trigger-button"
            >
                <Briefcase className="text-[#4ABDBD] w-5 h-5 flex-shrink-0" data-testid="profession-select-briefcase-icon" />
                <div className="flex-1 flex flex-wrap gap-1 items-center text-left" data-testid="profession-select-tags-container">
                    {selectedProfessions.length > 0 ? (
                        selectedProfessions.map(prof => (
                            <span
                                key={prof.professionId || prof._id}
                                className="inline-flex items-center gap-1 bg-[#4ABDBD]/10 text-[#4ABDBD] text-xs font-semibold px-2 py-1 rounded-lg"
                                data-testid={`profession-select-selected-tag-${prof.professionId || prof._id}`}
                            >
                                <ProfessionName prof={prof} />
                                {!disabled && (
                                    <X
                                        className="w-3 h-3 cursor-pointer hover:text-red-500 transition-colors"
                                        onClick={(e) => handleRemove(e, prof.professionId || prof._id)}
                                        data-testid={`profession-select-remove-icon-${prof.professionId || prof._id}`}
                                    />
                                )}
                            </span>
                        ))
                    ) : (
                        <span className="text-gray-400 truncate" data-testid="profession-select-placeholder-text">
                            {placeholder || t("common.select_profession")}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                    {loading && (
                        <span className="w-4 h-4 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin" data-testid="profession-select-loading-spinner"></span>
                    )}
                    <span className="text-[10px] text-gray-400 font-medium" data-testid="profession-select-counter-text">{selectedValues.length}/{maxSelections}</span>
                    {selectedValues.length > 0 && !disabled && !loading && (
                        <span
                            onClick={handleClearAll}
                            className="p-0.5 hover:bg-gray-100 rounded-full transition-colors"
                            data-testid="profession-select-clear-all-button"
                        >
                            <X className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" data-testid="profession-select-clear-all-icon" />
                        </span>
                    )}
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-testid="profession-select-chevron-icon" />
                </div>
            </button>

            {/* Dropdown — portaled and positioned from the trigger rect. The trigger
                grows as tags are added, so a panel pinned with top-full moved with
                it; measuring on open keeps the panel attached wherever it ends up. */}
            {panelOpen && typeof document !== 'undefined' && createPortal(
                <div
                    ref={panelRef}
                    style={panelStyle}
                    className="flex flex-col bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150"
                    data-testid="profession-select-dropdown"
                >
                    {/* Search Input */}
                    <div className="p-2 border-b border-gray-100 flex-shrink-0" data-testid="profession-select-search-section">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" data-testid="profession-select-search-icon" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value.replace(/[<>]/g, ''))}
                                placeholder={t("common.search", "Search...")}
                                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/30 focus:border-[#4ABDBD] bg-gray-50 placeholder-gray-400"
                                maxLength={50}
                                data-testid="profession-select-search-input"
                            />
                            {searchQuery && (
                                <button
                                    type="button"
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-0.5 hover:bg-gray-200 rounded-full transition-colors"
                                    data-testid="profession-select-search-clear-button"
                                >
                                    <X className="w-3.5 h-3.5 text-gray-400" data-testid="profession-select-search-clear-icon" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Options List */}
                    <div className="max-h-48 min-h-0 overflow-y-auto custom-scrollbar" data-testid="profession-select-options-list">
                        {loading ? (
                            <div className="flex items-center justify-center py-6" data-testid="profession-select-options-loading">
                                <span className="w-5 h-5 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                                <span className="ml-2 text-sm text-gray-500" data-testid="profession-select-options-loading-text">{t("common.loading")}</span>
                            </div>
                        ) : filteredProfessions.length === 0 ? (
                            <div className="py-6 text-center text-sm text-gray-500" data-testid="profession-select-no-results-text">
                                {searchQuery
                                    ? t("common.no_professions_match")
                                    : t("common.no_professions_available")}
                            </div>
                        ) : (
                            filteredProfessions.map((prof) => {
                                const profId = prof.professionId || prof._id;
                                const isSelected = selectedValues.includes(profId);
                                const isMaxReached = selectedValues.length >= maxSelections && !isSelected;
                                return (
                                    <button
                                        key={profId}
                                        type="button"
                                        onClick={() => !isMaxReached && handleSelect(profId)}
                                        disabled={isMaxReached}
                                        className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm transition-all duration-150 ${
                                            isSelected
                                                ? 'bg-[#4ABDBD]/10 text-[#4ABDBD] font-semibold'
                                                : isMaxReached
                                                    ? 'text-gray-300 cursor-not-allowed'
                                                    : 'text-gray-700 hover:bg-gray-50 cursor-pointer'
                                        }`}
                                        data-testid={`profession-select-option-${profId}`}
                                    >
                                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                                            isSelected ? 'bg-[#4ABDBD] border-[#4ABDBD]' : 'border-gray-300'
                                        }`}>
                                            {isSelected && <Check className="w-3 h-3 text-white" data-testid={`profession-select-option-check-icon-${profId}`} />}
                                        </div>
                                        <span className="truncate flex-1 text-left"><ProfessionName prof={prof} /></span>
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default ProfessionSelect;
